import { PULSE_STEPS, layerOf, type CrackleLayer, type PillarLayer, type SparkPath, type SparksLayer, type VfxDefinition, type VfxLayer } from "./definition.ts";

/**
 * What a vfx shows at a time since it started (ADR-134), read from its definition, as numbers in art pixels: the drawing
 * reads them and decides nothing, and every moment of a definition is tested through here. Every function writes into a
 * state made once, so a frame allocates nothing.
 */

/**
 * How many steps the edge glow fades in, and so the strongest step.
 */
export const EDGE_STEPS = 4;

/**
 * How many steps the aura rises in, and so the strongest step.
 */
export const AURA_STEPS = 3;

/**
 * What a vfx shows at one moment: the ring's radius in art pixels, 0 for none, and whether it is the bright one; the
 * aura's step from 0 to `AURA_STEPS`; the pillar's half width, 0 for none, and how much of its height has grown, 0 to 1;
 * where the rays' inner and outer ends stand from the middle of the body, 0 for no rays, and how many rays; the sparks'
 * clock in milliseconds, -1 for none; the sign's rise in art pixels, -1 when it is not shown; and the edge glow's step
 * from 0 to `EDGE_STEPS`.
 */
export interface VfxState {
  ringRadius: number;
  ringBright: boolean;
  auraStep: number;
  pillarHalf: number;
  pillarGrow: number;
  rayFrom: number;
  rayTo: number;
  rayCount: number;
  sparkClock: number;
  signRise: number;
  edgeStep: number;
}

/**
 * A state to write into.
 *
 * @returns A state that shows nothing.
 */
export function newVfxState(): VfxState {
  return { ringRadius: 0, ringBright: false, auraStep: 0, pillarHalf: 0, pillarGrow: 0, rayFrom: 0, rayTo: 0, rayCount: 0, sparkClock: -1, signRise: -1, edgeStep: 0 };
}

/**
 * The progress of a time through a span, 0 to 1.
 *
 * @param t The time.
 * @param from The span's start.
 * @param to The span's end.
 * @returns The share of the span gone.
 */
function across(t: number, from: number, to: number): number {
  return (t - from) / (to - from);
}

/**
 * The aura's strength at a time, from its keys; 0 outside them.
 *
 * @param keys The `[ms, strength]` points.
 * @param t The time.
 * @returns The strength.
 */
function auraAt(keys: readonly (readonly [number, number])[], t: number): number {
  for (let index = 1; index < keys.length; index += 1) {
    const a = keys[index - 1] as readonly [number, number];
    const b = keys[index] as readonly [number, number];
    if (b[0] > a[0] && t >= a[0] && t < b[0]) {
      return a[1] + (b[1] - a[1]) * across(t, a[0], b[0]);
    }
  }
  return 0;
}

/**
 * The pillar's half width at a time: full to its hold, then two thirds and one third, in thirds of the narrowing.
 *
 * @param layer The pillar.
 * @param t The time.
 * @returns The half width.
 */
function pillarHalfAt(layer: PillarLayer, t: number): number {
  const fade = t < layer.holdUntilMs ? 1 : 1 - across(t, layer.holdUntilMs, layer.endMs);
  return Math.round(layer.half * (fade > 0.66 ? 1 : fade > 0.33 ? 2 / 3 : 1 / 3));
}

/**
 * Writes one layer's part of the state for a time.
 *
 * @param layer The layer.
 * @param t The time since the vfx started.
 * @param out The state.
 */
function applyLayer(layer: VfxLayer, t: number, out: VfxState): void {
  if (layer.kind === "ring") {
    if (t >= layer.fromMs && t < layer.toMs && out.ringRadius === 0) {
      out.ringRadius = Math.round(layer.radiusFrom + (layer.radiusTo - layer.radiusFrom) * across(t, layer.fromMs, layer.toMs));
      out.ringBright = layer.bright;
    }
  } else if (layer.kind === "aura") {
    out.auraStep = Math.round((Math.max(0, auraAt(layer.keys, t)) / layer.peak) * AURA_STEPS);
  } else if (layer.kind === "pillar") {
    if (t >= layer.fromMs && t < layer.endMs) {
      out.pillarHalf = pillarHalfAt(layer, t);
      out.pillarGrow = Math.min(1, (t - layer.fromMs) / layer.growMs);
    }
  } else if (layer.kind === "rays") {
    if (t >= layer.fromMs && t < layer.fromMs + layer.spanMs) {
      const p = across(t, layer.fromMs, layer.fromMs + layer.spanMs);
      out.rayFrom = Math.round(layer.inner[0] + (layer.inner[1] - layer.inner[0]) * p);
      out.rayTo = Math.round(layer.outer[0] + (layer.outer[1] - layer.outer[0]) * p);
      out.rayCount = layer.count;
    }
  } else if (layer.kind === "sparks") {
    if (t >= layer.fromMs && t < layer.fromMs + layer.spanMs) {
      out.sparkClock = t - layer.fromMs;
    }
  } else if (layer.kind === "text") {
    const dark = layer.gap !== null && t >= layer.gap[0] && t < layer.gap[1];
    if (t >= layer.fromMs && t < layer.toMs && !dark) {
      out.signRise = Math.min(layer.risePx, Math.floor((t - layer.fromMs) / layer.riseMs * layer.risePx));
    }
  } else if (layer.kind === "edge" && t >= layer.fromMs && t < layer.toMs) {
    out.edgeStep = Math.max(1, Math.round((1 - across(t, layer.fromMs, layer.toMs)) * EDGE_STEPS));
  }
}

/**
 * Writes what a vfx shows at a time.
 *
 * @param definition The vfx.
 * @param elapsed Milliseconds since it started; a time before it is its first frame.
 * @param reduced Whether motion is reduced, which takes the definition's reduced form.
 * @param out The state to write.
 * @returns True while the vfx lasts, false once it is over and `out` is left as it was.
 */
export function vfxState(definition: VfxDefinition, elapsed: number, reduced: boolean, out: VfxState): boolean {
  const t = Math.max(0, elapsed);
  out.ringRadius = 0;
  out.ringBright = false;
  out.auraStep = 0;
  out.pillarHalf = 0;
  out.pillarGrow = 0;
  out.rayFrom = 0;
  out.rayTo = 0;
  out.rayCount = 0;
  out.sparkClock = -1;
  out.signRise = -1;
  out.edgeStep = 0;
  if (reduced) {
    const form = definition.reduced;
    if (t >= form.lifeMs) {
      return false;
    }
    const flash = t < form.flashMs;
    out.auraStep = flash ? form.auraStep : 0;
    out.pillarHalf = flash ? form.pillarHalf : 0;
    out.pillarGrow = flash && form.pillarHalf > 0 ? 1 : 0;
    out.edgeStep = flash ? form.edgeStep : 0;
    const text = layerOf(definition, "text");
    out.signRise = form.textStill && text !== undefined ? text.risePx : -1;
    return true;
  }
  if (t >= definition.lifeMs) {
    return false;
  }
  for (let index = 0; index < definition.layers.length; index += 1) {
    applyLayer(definition.layers[index] as VfxLayer, t, out);
  }
  return true;
}

/**
 * A spark at a moment: where it stands from the middle of the body in art pixels, the length of its plus's arms (2, 1,
 * or 0 for a single dot) and whether it is the light tone.
 */
export interface SparkState {
  dx: number;
  dy: number;
  arm: number;
  light: number;
}

/**
 * A spark state to write into.
 *
 * @returns A spark at the middle with no arms.
 */
export function newSparkState(): SparkState {
  return { dx: 0, dy: 0, arm: 0, light: 0 };
}

/**
 * Writes a spark's state at the sparks' clock.
 *
 * @param layer The sparks layer.
 * @param index The spark, an index into the layer's paths.
 * @param clock The sparks' clock in milliseconds, from `VfxState.sparkClock`.
 * @param out The state to write.
 * @returns True while the spark is in flight, false before it starts, after it ends and for an index the layer lacks,
 *   leaving `out` as it was.
 */
export function sparkState(layer: SparksLayer, index: number, clock: number, out: SparkState): boolean {
  const path: SparkPath | undefined = layer.paths[index];
  if (path === undefined) {
    return false;
  }
  const lived = clock - path.delayMs;
  if (lived < 0 || lived >= path.lifeMs) {
    return false;
  }
  const k = lived / path.lifeMs;
  out.dx = Math.round(path.x0 + (path.x1 - path.x0) * k);
  out.dy = Math.round(path.y0 + (path.y1 - path.y0) * k);
  out.arm = k < layer.armsUntil[0] ? 2 : k < layer.armsUntil[1] ? 1 : 0;
  out.light = path.light;
  return true;
}


/**
 * What a bar vfx shows at one moment: where the wave's leading edge stands along the bar as a share of the bar's width
 * plus the wave's own width (0 to 1, -1 for no wave) and the wave's width in logical pixels, the pulse's step from 0 to
 * `PULSE_STEPS`, and the crackles' clock in milliseconds, -1 for none.
 */
export interface BarState {
  wavePos: number;
  waveWidth: number;
  pulseStep: number;
  crackleClock: number;
}

/**
 * A bar state to write into.
 *
 * @returns A state that shows nothing.
 */
export function newBarState(): BarState {
  return { wavePos: -1, waveWidth: 0, pulseStep: 0, crackleClock: -1 };
}

/**
 * Writes what a bar vfx shows at a time.
 *
 * @param definition The vfx, whose target is the XP bar.
 * @param elapsed Milliseconds since it started; a time before it is its first frame.
 * @param reduced Whether motion is reduced, which takes the definition's reduced form: a still pulse for the flash.
 * @param out The state to write.
 * @returns True while the vfx lasts, false once it is over and `out` is left as it was.
 */
export function barState(definition: VfxDefinition, elapsed: number, reduced: boolean, out: BarState): boolean {
  const t = Math.max(0, elapsed);
  out.wavePos = -1;
  out.waveWidth = 0;
  out.pulseStep = 0;
  out.crackleClock = -1;
  if (reduced) {
    const form = definition.reduced;
    if (t >= form.lifeMs) {
      return false;
    }
    out.pulseStep = t < form.flashMs ? form.pulseStep : 0;
    return true;
  }
  if (t >= definition.lifeMs) {
    return false;
  }
  for (let index = 0; index < definition.layers.length; index += 1) {
    const layer = definition.layers[index] as VfxLayer;
    if (layer.kind === "wave") {
      if (t >= layer.fromMs && t < layer.toMs) {
        out.wavePos = (across(t, layer.fromMs, layer.toMs) * layer.runs) % 1;
        out.waveWidth = layer.widthPx;
      }
    } else if (layer.kind === "pulse") {
      out.pulseStep = Math.round((Math.max(0, auraAt(layer.keys, t)) / layer.peak) * PULSE_STEPS);
    } else if (layer.kind === "crackle" && t >= layer.fromMs && t < layer.fromMs + layer.spanMs) {
      out.crackleClock = t - layer.fromMs;
    }
  }
  return true;
}

/**
 * A crackle at a moment: its place along the bar as a share of the bar's width, and its rise (and fall back) and drift in logical
 * pixels from the bar's top edge, its size, and whether it is the light tone.
 */
export interface CrackleState {
  at: number;
  dx: number;
  dy: number;
  size: number;
  light: number;
}

/**
 * A crackle state to write into.
 *
 * @returns A crackle on the bar's edge.
 */
export function newCrackleState(): CrackleState {
  return { at: 0, dx: 0, dy: 0, size: 1, light: 0 };
}

/**
 * Writes a crackle's state at the crackles' clock.
 *
 * @param layer The crackle layer.
 * @param index The crackle, an index into the layer's list.
 * @param clock The clock in milliseconds, from `BarState.crackleClock`.
 * @param out The state to write.
 * @returns True while it is alive, false before it starts, after it ends and for an index the layer lacks, leaving `out`
 *   as it was.
 */
export function crackleState(layer: CrackleLayer, index: number, clock: number, out: CrackleState): boolean {
  const crackle = layer.crackles[index];
  if (crackle === undefined) {
    return false;
  }
  const lived = clock - crackle.delayMs;
  if (lived < 0 || lived >= crackle.lifeMs) {
    return false;
  }
  const k = lived / crackle.lifeMs;
  out.at = crackle.at;
  out.dx = Math.round(crackle.drift * k);
  out.dy = Math.round(crackle.rise * k + crackle.fall * k * k);
  out.size = crackle.size;
  out.light = crackle.light;
  return true;
}
